import { createSlice } from '@reduxjs/toolkit';
import { ADapi } from '../services/ADapi';

import findUsersInOtherGroups from '../utils/findUsersInOtherGroups';

const initialState = {
  tree: [],
  selectedUsers: {},
  groups: [],
  selectedUsersIdsForBackend: [],
  allSelected: [],
  totalUsersCount: 0,
  selectedGroups: {},
  arrayOfSelectedItems: [],
};

const selectUserInGroups = (state, userId) => {
  const foundUsers = findUsersInOtherGroups(state.groups, userId);

  for (let user in foundUsers) {
    const groupId = user.match(/\d+/g)[1];

    if (!state.selectedGroups[groupId]) {
      state.selectedGroups[groupId] = {};
    }

    state.selectedGroups[groupId][user] = null;
  }
};

const unselectUserInGroups = (state, userId) => {
  const foundUsers = findUsersInOtherGroups(state.groups, userId);

  for (let user in foundUsers) {
    const groupId = user.match(/\d+/g)[1];

    if (state.selectedGroups[groupId]) {
      delete state.selectedGroups[groupId][user];
    }
  }
};

const treeSlice = createSlice({
  name: 'tree',
  initialState,
  reducers: {
    updateGroups(state, action) {
      state.groups = action.payload;
    },
    treeUpdated(state, action) {
      state.tree = action.payload;
    },
    updateSelectedGroup(state, action) {
      const itemId = action.payload;
      state.selectedGroups[itemId] = null;

      const groupId = +itemId.split('groupId')[1];
      const group = state.groups.find((group) => group.id === groupId);

      group?.ad_users.forEach((userId) => {
        selectUserInGroups(state, userId);
      });
    },
    undoSelectedGroup(state, action) {
      const itemId = action.payload;
      const groupId = +itemId.split('groupId')[1];

      for (let user in state.selectedGroups[groupId]) {
        const userId = user.match(/\d+/g)[2];

        delete state.selectedGroups[groupId][user];

        unselectUserInGroups(state, userId);
      }
    },
    resetSelectedGroups(state) {
      state.selectedGroups = {};
    },
    parseUserIdsForBackend(state) {
      const userIds = new Set();

      state.arrayOfSelectedItems.forEach((user) => {
        if (!user.includes('groupId')) {
          const parsedId = +user.split('u')[1];

          // Sometimes a parsed id is equal to 0. Research and resolve.
          if (parsedId > 0) {
            userIds.add(parsedId);
          }
        }
      });

      state.selectedUsersIdsForBackend = Array.from(userIds);
    },
    updateUsersOfSelectedGroup(state, action) {
      const selectedGroup = action.payload;

      selectedGroup?.ad_users.forEach((userId) => {
        selectUserInGroups(state, userId);

        const userKey = `d${selectedGroup.domain}-g${selectedGroup.id}-u${userId}`;
        state.selectedUsers[userKey] = null;
      });

      state.totalUsersCount = selectedGroup?.ad_users?.length || 0;
    },
    undoUsersOfSelectedGroup(state, action) {
      const groupId = +action.payload;

      for (let user in state.selectedGroups[groupId]) {
        const userId = user.match(/\d+/g)[2];

        unselectUserInGroups(state, userId);
      }
    },
    selectAllUsersOfSelectedGroup(state, action) {
      const groupId = +action.payload;
      const foundGroup = state.groups.find((group) => group.id === groupId);

      foundGroup?.ad_users.forEach((userId) => {
        selectUserInGroups(state, userId);
      });
    },
    updateSelectedGroups(state, action) {
      const userId = action.payload.match(/\d+/g)[2];

      selectUserInGroups(state, userId);
    },
    undoUsers(state, action) {
      const userId = action.payload.match(/\d+/g)[2];

      unselectUserInGroups(state, userId);
    },
    createArrayFromSelectedGroups(state) {
      const arrayOfSelectedItems = Object.values(state.selectedGroups).reduce(
        (acc, current) => {
          for (let item in current) {
            acc.push(item);
          }

          return acc;
        },
        []
      );

      state.arrayOfSelectedItems = arrayOfSelectedItems;
    },
  },
  extraReducers: (builder) => {
    builder.addMatcher(
      ADapi.endpoints.getGroups.matchFulfilled,
      (state, action) => {
        // action.payload contains the data returned by the query
        state.groups = action.payload;
      }
    );
  },
});

export const {
  updateGroups,
  treeUpdated,
  updateSelectedGroup,
  undoSelectedGroup,
  resetSelectedGroups,
  parseUserIdsForBackend,
  selectAll,
  updateUsersOfSelectedGroup,
  undoUsersOfSelectedGroup,
  selectAllUsersOfSelectedGroup,
  updateSelectedGroups,
  setSelected,
  undoUsers,
  createArrayFromSelectedGroups,
} = treeSlice.actions;
export default treeSlice.reducer;
